import type { ReactNode } from 'react';
import React from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Alert, Typography } from '@mui/material';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  AlineIntroVideo,
  GenerateAiPlaybook,
  LaunchNdaTemplate,
  SignPDF,
  UploadDocumentsTour,
  ViewReport,
} from './steps';
import { AlineButton, AlineTooltip } from '../generic';
import { ExpandIcon } from '../icons/ExpandIcon';
import { SuccessTickIcon } from '../icons/GetStarted';

const useStyles = makeStyles<{ open: boolean }>()((theme, { open }) => ({
  accordion: {
    '&::before': {
      display: 'none',
    },
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: 3,
    },
    border: open ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
    borderRadius: 3,
    boxSizing: 'border-box',
    width: '100%',
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    padding: theme.spacing(0, 6, 3, 6),
  },
  accordionDetailsContent: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  accordionSummary: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'center',
  },
  accordionSummaryContent: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(4),
  },
  process: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.primary}`,
    borderRadius: '50%',
    display: 'flex',
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  progressBar: {
    width: '100%',
  },
}));

export function StepAccordion({
  expanded = '',
  onboardingTask,
  setExpanded,
  stepNum,
}: {
  expanded: string | null;
  onboardingTask: Apollo.OnboardingTask;
  setExpanded: React.Dispatch<React.SetStateAction<string | null>>;
  stepNum: number;
}) {
  const isComplete = Boolean(onboardingTask.completedAt);
  const { classes } = useStyles({ open: expanded === onboardingTask.id });

  return (
    <Accordion
      className={classes.accordion}
      elevation={0}
      expanded={expanded === onboardingTask.id}
      onChange={(_event, newExpanded) => {
        setExpanded(newExpanded ? onboardingTask.id : null);
      }}
    >
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={<ExpandIcon fill="#146EE8" />}
      >
        <div className={classes.accordionSummaryContent}>
          {isComplete ? <SuccessTickIcon /> : <StepNumber value={stepNum} />}
          <StepTitle action={onboardingTask.action} />
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {isComplete && <Alert severity="success">This step is complete.</Alert>}
        <GetOnboardingTaskActionComponent task={onboardingTask} />
      </AccordionDetails>
    </Accordion>
  );
}

function StepNumber({ value }: { value: number }) {
  const { classes } = useStyles({ open: true });

  return (
    <div className={classes.process}>
      <Typography variant="subtitle2">{value}</Typography>
    </div>
  );
}

const useGenericActionStyles = makeStyles()(theme => ({
  grid: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
  },
  imageContainer: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  leftItem: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(2),
  },
  tooltip: {
    width: 'fit-content',
  },
}));

interface GenericActionProps {
  actionTitle: string;
  onClick: () => void;
  disabledReason?: string;
  image?: ReactNode;
  loading?: boolean;
  text?: string;
}

export function GenericAction({
  actionTitle,
  disabledReason = '',
  image: _image,
  loading,
  onClick,
  text = 'Familiarize yourself with our mission, values, and culture. Dive into our website and review any materials provided to gain a solid understanding of who we are and what we stand for.',
}: GenericActionProps) {
  const { classes } = useGenericActionStyles();

  return (
    <div className={classes.grid}>
      <div className={classes.leftItem}>
        <Typography>{text}</Typography>
        <AlineTooltip arrow className={classes.tooltip} placement="top" title={disabledReason}>
          <div>
            <AlineButton
              color="primary"
              disabled={Boolean(disabledReason)}
              loading={loading}
              variant="contained"
              onClick={onClick}
            >
              {actionTitle}
            </AlineButton>
          </div>
        </AlineTooltip>
      </div>
    </div>
  );
}

function GetOnboardingTaskActionComponent({ task }: { task: Apollo.OnboardingTask }): JSX.Element {
  switch (task.action) {
    case Apollo.OnboardingTaskAction.LaunchExampleNdaPlaybook:
      return <LaunchNdaTemplate task={task} />;
    case Apollo.OnboardingTaskAction.AlineIntroVideo:
      return <AlineIntroVideo task={task} />;
    case Apollo.OnboardingTaskAction.SignPdf:
      return <SignPDF task={task} />;
    case Apollo.OnboardingTaskAction.ImportLegacyIntoRepo:
      return <UploadDocumentsTour task={task} />;
    case Apollo.OnboardingTaskAction.AlineAiExampleReport:
      return <ViewReport task={task} />;
    case Apollo.OnboardingTaskAction.GenerateAiPlaybook:
      return <GenerateAiPlaybook task={task} />;
  }
}

function StepTitle({ action }: { action: Apollo.OnboardingTaskAction }): JSX.Element {
  switch (action) {
    case Apollo.OnboardingTaskAction.ImportLegacyIntoRepo:
      return <Typography>Import documents into your AI repository</Typography>;
    case Apollo.OnboardingTaskAction.LaunchExampleNdaPlaybook:
      return <Typography>Check out an Aline AI playbook in action</Typography>;
    case Apollo.OnboardingTaskAction.AlineIntroVideo:
      return <Typography>See what you can do with Aline</Typography>;
    case Apollo.OnboardingTaskAction.SignPdf:
      return <Typography>Sign any document using AlineSign</Typography>;
    case Apollo.OnboardingTaskAction.AlineAiExampleReport:
      return <Typography>View an example AI report</Typography>;
    case Apollo.OnboardingTaskAction.GenerateAiPlaybook:
      return <Typography>Generate an AI playbook off your own template</Typography>;
  }
}
